import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import {
    LineItem,
    RapidStorBoxShopCouponResponse,
} from '@r6digital/rapidstor-types/types/Merchandise';

import { useGetBoxShopCouponMutation } from '@/core/api/rapidstor';
import { logEvent } from '@/core/utils/logging';
import { RapidStorSettings } from '@/core/utils/rapidstor';

import Button from '@/core/components/elements/button';
import Spinner from '@/core/components/elements/spinner';

import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';

import classes from './styles.module.scss';

interface Props {
    cart: LineItem[] | undefined;
    pickupLocation: string;
    boxShopDiscount: number;
    updateCartItem: (id: string, quantity: number) => void;
    updatePickupLocation: (location: string) => void;
    setBoxShopDiscount: (discount: RapidStorBoxShopCouponResponse) => void;
}

const FALLBACK_PRODUCT_PICTURE = 'https://ik.imagekit.io/bytcx9plm/120x120.png';

// Product pictures are stored up to 600px tall for StorApp, but render at
// 60–120px here. Ask ImageKit for a 2x thumbnail; c-at_max never upscales.
// Any existing tr value is kept and chained (ImageKit's ":" separator).
const THUMBNAIL_TRANSFORM = 'h-240,c-at_max';
const productThumbnail = (picture: string) => {
    if (!picture.startsWith('https://ik.imagekit.io/')) return picture;
    try {
        const url = new URL(picture);
        const existing = url.searchParams.get('tr');
        url.searchParams.set('tr', existing ? `${existing}:${THUMBNAIL_TRANSFORM}` : THUMBNAIL_TRANSFORM);
        return url.toString();
    } catch {
        return picture;
    }
};

export default function Merchandise({
    cart,
    pickupLocation,
    boxShopDiscount,
    updateCartItem,
    updatePickupLocation,
    setBoxShopDiscount,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { firebaseLogEvent } = useContext(AnalyticsContext);

    const [showAll, setShowAll] = useState<boolean>(false);
    const [code, setCode] = useState<string>();
    const [error, setError] = useState<string | undefined>();
    const [couponLoading, setCouponLoading] = useState<boolean>(false);

    const [sendBoxShopCouponRequest] = useGetBoxShopCouponMutation();

    const sliceAmount = useMemo(() => {
        if (showAll) {
            return [0, cart?.length];
        }
        return [0, 2];
    }, [showAll, cart?.length]);

    const productQuantityChange = (type: 'add' | 'remove', product: LineItem) => {
        if (type === 'add') {
            if (product.quantity < product.inventory) {
                updateCartItem(product._id, product.quantity + 1);
            }
        } else if (product.quantity >= 1) {
            updateCartItem(product._id, product.quantity - 1);
        }
    };

    const showCoupon = useMemo(() => {
        if (!RapidStorSettings(location).boxshop().coupons().show()) {
            return false;
        }

        const totalCartItems = cart?.reduce((acc, item) => acc + item.quantity, 0);

        if (!totalCartItems) {
            return false;
        }

        if (totalCartItems > 0) {
            return true;
        }

        return false;
    }, [cart, location]);

    const productPrice = (price: number, tax: number) => {
        const discountAmount = price * (boxShopDiscount / 100);
        return price - discountAmount + tax;
    };

    const fetchBoxShopCoupon = async () => {
        setCouponLoading(true);
        try {
            const result = await sendBoxShopCouponRequest({
                corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                siteToken: window.rapidstor_settings.rapidstor_options.locationToken,
                code,
            }).unwrap();

            setBoxShopDiscount(result);
            setError(undefined);
            setCouponLoading(false);
        } catch (err) {
            logEvent(2, `[BoxShop] Error fetching coupon: ${err}`, location);
            setError('Promo code expired or invalid');
            setCouponLoading(false);
        }
    };

    return (
        <>
            <span className={`${classes.header_text}`} data-testid="boxshop-section">
                {t('wizard:boxShop:title')}
            </span>

            <div className={`${classes.products_container}`}>
                {cart
                    && cart.length > 0
                    && cart
                        .slice(sliceAmount[0], sliceAmount[1])
                        .map((product: LineItem) => (
                            <div
                                className={`${classes.product_item} ${product.quantity > 0 && classes.active
                                    }`}
                                key={product.key}
                            >
                                <div className={`${classes.image}`}>
                                    <img
                                        alt="Product"
                                        src={productThumbnail(product.picture ?? FALLBACK_PRODUCT_PICTURE)}
                                    />
                                </div>
                                <div className={`${classes.detail}`}>
                                    <span className={`${classes.name}`}>{product.name}</span>
                                    <div className={`${classes.pricing}`}>
                                        <span
                                            className={`${classes.amount} ${boxShopDiscount > 0 && classes.discounted
                                                }`}
                                        >
                                            {`${location.rapidstor.currencySymbol}${productPrice(
                                                product.price,
                                                product.tax,
                                            ).toFixed(2)}`}
                                        </span>

                                        {product.inStock ? (
                                            <div className={`${classes.qtySelector}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => productQuantityChange('remove', product)}
                                                >
                                                    <svg
                                                        width="10"
                                                        height="10"
                                                        viewBox="0 0 10 1"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <rect
                                                            width="10"
                                                            height="1"
                                                            rx="0.5"
                                                            fill="#444"
                                                        />
                                                    </svg>
                                                </button>
                                                <span>{product.quantity}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => productQuantityChange('add', product)}
                                                >
                                                    <svg
                                                        width="10"
                                                        height="10"
                                                        viewBox="0 0 10 10"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            // eslint-disable-next-line max-len
                                                            d="M10 5C10 5.11051 9.9561 5.21649 9.87796 5.29463C9.79982 5.37277 9.69384 5.41667 9.58333 5.41667H5.41667V9.58333C5.41667 9.69384 5.37277 9.79982 5.29463 9.87796C5.21649 9.9561 5.11051 10 5 10C4.88949 10 4.78351 9.9561 4.70537 9.87796C4.62723 9.79982 4.58333 9.69384 4.58333 9.58333V5.41667H0.416667C0.30616 5.41667 0.200179 5.37277 0.122039 5.29463C0.0438988 5.21649 0 5.11051 0 5C0 4.88949 0.0438988 4.78351 0.122039 4.70537C0.200179 4.62723 0.30616 4.58333 0.416667 4.58333H4.58333V0.416667C4.58333 0.30616 4.62723 0.200179 4.70537 0.122039C4.78351 0.0438988 4.88949 0 5 0C5.11051 0 5.21649 0.0438988 5.29463 0.122039C5.37277 0.200179 5.41667 0.30616 5.41667 0.416667V4.58333H9.58333C9.69384 4.58333 9.79982 4.62723 9.87796 4.70537C9.9561 4.78351 10 4.88949 10 5Z"
                                                            fill="#444"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <span className={`${classes.outOfStock}`}>
                                                {t('wizard:boxShop:outOfStock')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
            </div>

            <div className={`${classes.button_container}`}>
                <div>
                    {error && <span className={`${classes.coupon_error}`}>{error}</span>}
                    <div className={`${classes.merch_coupon}`}>
                        {showCoupon && (
                            <>
                                <input
                                    type="text"
                                    placeholder="Coupon Code for Merchandise"
                                    value={code ?? ''}
                                    disabled={couponLoading}
                                    onChange={(e) => setCode(e.target.value)}
                                    className={classes.coupon_input}
                                />
                                <button
                                    type="button"
                                    disabled={!code || couponLoading}
                                    onClick={fetchBoxShopCoupon}
                                    className={classes.coupon_button}
                                >
                                    {couponLoading ? (
                                        <Spinner size="24" />
                                    ) : (
                                        `${t('coupon_code:apply')}`
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {cart && cart.length > 2 ? (
                    <Button
                        type="primary"
                        size="medium"
                        disableScroll
                        onclick={() => {
                            if (showAll) {
                                firebaseLogEvent('show_all_products', {});
                            }
                            setShowAll(!showAll);
                        }}
                    >
                        {showAll
                            ? t('wizard:boxShop:showLess')
                            : t('wizard:boxShop:showAll')}
                    </Button>
                ) : null}
            </div>

            {showCoupon && (
                <div className={`${classes.pickup_container}`}>
                    <span className={`${classes.header_text}`}>
                        {t('wizard:boxShop:pickUpLocation')}
                    </span>
                    {location.rapidstor.legacyPickupLocationsEnabled ? (
                        <div className={`${classes.pickupLocations}`}>
                            {location.rapidstor.legacyPickupLocations
                                .split(',')
                                .map((loc, index) => (
                                    <div key={`${index}_lpL_idx`}>
                                        <div className={`${classes.custom_checkbox}`}>
                                            <input
                                                id={`${index}_pickupLoc_idx`}
                                                type="checkbox"
                                                checked={pickupLocation === loc}
                                                onChange={() => updatePickupLocation(loc)}
                                            />
                                            <label htmlFor={`${index}_pickupLoc_idx`} />
                                        </div>
                                        <span>{loc}</span>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <span>
                            {`${location.address.address1}, ${location.address.city}, ${location.address.postalCode}, ${location.address.region}, ${location.address.country}`}
                        </span>
                    )}
                </div>
            )}
        </>
    );
}
